/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
// hud should be visible when either of the following is true -
// 1. it is in docking mode
// 2. mouse is over it
// 3. it is in focus (via keyboard / otherwise)

import {
  HIDE_HUD_ICONS_THRESHOLD,
  HIDE_HUD_ICONS_MIN_THRESHOLD,
  PRESET_HIDE_HUD_ICONS_THRESHOLD,
  HUD_HEIGHT,
  HUD_BOTTOM_MARGIN
} from "constants/constants";
import { appIdentifiers, docViewModes, contextMenuPlacements, SWITCH_PREVIEW_STATE } from "constants/enums";
import { getShowSearchInputUI, getShouldshowSearchInHud } from "selectors/searchSelectors";
import { getViewPortWidthWithoutPagesViewScrollbar, getDocumentVisibleHeight } from "selectors/documentViewSelectors";
import {
  isCurrentDeviceMobile,
  isCurrentDeviceTablet,
  isIOS,
  isIpad,
  getOS
} from "dc-preview-utils/src/Utils/DeviceUtils";
import { getIsInPlaceMode, getViewMode } from "selectors/viewModeSelectors";
import {
  isSinglePageMode,
  getTotalPages,
  isPageByPageViewMode,
  getShouldUnblockUIFeatures,
  isFilePasswordProtected,
  getFileExtension,
  isFitOneFullPageMode
} from "selectors/documentSelectors";
import {
  getIsParentWebsiteScrolling,
  isDownloadFeatureEnabled,
  getShouldUseModernViewer,
  getSwitchPreviewState
} from "selectors/appSelectors";
import downloadIcon from "dc-icons/viewer2021/SDC_Download_18_N.svg";
import printIcon from "dc-icons/viewer2021/SDC_Print_18_N.svg";
import { downloadAssetFromDocApi } from "utils/DownloadUtility";
import { printHandler } from "print/printHandler";
import { isFirstPageLoaded } from "selectors/pagedViewState";

export const getHudHandle = stores => stores.hudStore.hudHandle;
export const hudDisabled = stores => {
  if (getIsInPlaceMode(stores)) {
    const { version } = getOS();
    if (isIpad() && getTotalPages(stores) > 30 && parseInt(version.substr(0, 2)) < 13) {
      /* on Ipad, a crash(on chrome)/reload(on safari) is occuring due to react-spectrum icon button for files above 30 pages, 
      so currently won't show HUD for such case, <ToDo> : An alternative can be to show our own buttons*/
      return true;
    }
    if (
      (getDocumentVisibleHeight(stores) >= HUD_HEIGHT + HUD_BOTTOM_MARGIN &&
        !stores.hudStore.isDisabled &&
        !getIsParentWebsiteScrolling(stores)) ||
      stores.hudStore.isFocused
    ) {
      return false;
    }
    return true;
  }
  if (isSinglePageMode(stores)) return stores.hudStore.isDisabled;
  return isCurrentDeviceMobile() || stores.hudStore.isDisabled;
};
export const shouldAutoHideHud = stores => stores.hudStore.shouldAutoHide;
export const animationInOutTimer = stores => stores.hudStore.animationInOutTimer;
export const isHudAnimationOut = stores => stores.hudStore.isAnimationOut;
export const getAnimationType = stores => stores.hudStore.animationType;

export const showHUD = stores =>
  stores.hudStore.isDocked ||
  stores.hudStore.isMouseOver ||
  !stores.hudStore.shouldAutoHide ||
  stores.hudStore.isFocused;

export const showPageNavigationInModernViewer = stores =>
  stores.hudStore.componentProperties.get(appIdentifiers.PAGE_NAVIGATION).visible;
export const showPageNumberUIInModernViewer = stores =>
  stores.hudStore.componentProperties.get(appIdentifiers.PAGE_NUMBER).visible;

export const showPageNavigation = stores =>
  stores.hudStore.componentProperties.get(appIdentifiers.PAGE_NAVIGATION).visible &&
  getIsHUDLargerThanViewPort(stores) &&
  !getIsSearchInputUIVisibleInHUD(stores);

export const showPageViewSwitcherVisibility = stores =>
  stores.hudStore.componentProperties.get(appIdentifiers.PAGE_VIEW_SWITCHER).visible;

export const showPageNumber = stores =>
  stores.hudStore.componentProperties.get(appIdentifiers.PAGE_NUMBER).visible &&
  !getIsSearchInputUIVisibleInHUD(stores);
export const showZoomUI = stores =>
  stores.hudStore.showZoomUI &&
  (showFitWidthMode(stores) || showFitPageMode(stores) || showFixedWidthMode(stores)) &&
  (getIsHUDLargerThanViewPort(stores) || !getIsSearchInputUIVisibleInHUD(stores));
export const showZoomControl = stores => stores.hudStore.componentProperties.get(appIdentifiers.ZOOM_CONTROL).visible;
export const showFitWidthMode = stores => stores.hudStore.componentProperties.get(appIdentifiers.FIT_WIDTH).visible;
export const showFitPageMode = stores => {
  if (getShouldUseModernViewer(stores)) return stores.hudStore.componentProperties.get(appIdentifiers.FIT_PAGE).visible;
  return (
    stores.hudStore.componentProperties.get(appIdentifiers.FIT_PAGE).visible &&
    getIsHUDLargerThanMinThresholdOfViewPort(stores)
  );
};
export const showFixedWidthMode = stores => stores.hudStore.componentProperties.get(appIdentifiers.FIXED_WIDTH).visible;
export const showPrint = stores =>
  stores.hudStore.componentProperties.get(appIdentifiers.PRINT).visible &&
  !(isCurrentDeviceMobile() || isCurrentDeviceTablet()) &&
  !isFilePasswordProtected(stores);
export const showDownloadButton = stores =>
  isDownloadFeatureEnabled(stores) &&
  getIsHUDLargerThanMinThresholdOfViewPort(stores) &&
  stores.hudStore.componentProperties.get(appIdentifiers.DOWNLOAD).visible &&
  !isIOS();
export const showDockButton = stores =>
  !(isCurrentDeviceMobile() || isCurrentDeviceTablet()) &&
  stores.hudStore.componentProperties.get(appIdentifiers.DOCK).visible &&
  getIsHUDLargerThanMinThresholdOfViewPort(stores);
export const getIsHUDDocked = stores => stores.hudStore.isDocked;
export const getShowZoomInOut = stores => getIsHUDLargerThanViewPort(stores) && showZoomControl(stores);

export const getShowSearchInHUD = stores =>
  getIsHUDLargerThanMinThresholdOfViewPort(stores) &&
  getShouldshowSearchInHud(stores) &&
  !stores.hudStore.isExplicitlyDisabled;

export const getIsSearchInputUIVisibleInHUD = stores => getShowSearchInHUD(stores) && getShowSearchInputUI(stores);

export const getShouldUpdateHUDPosition = stores => stores.hudStore.shouldUpdateHUDPosition;

export const getIsHUDLargerThanViewPort = stores => {
  // for in place mode HUD width is constant
  if (getIsInPlaceMode(stores)) return true;
  const limit = isSinglePageMode(stores) ? PRESET_HIDE_HUD_ICONS_THRESHOLD : HIDE_HUD_ICONS_THRESHOLD;
  return getViewPortWidthWithoutPagesViewScrollbar(stores) > limit;
};

export const getIsHUDLargerThanMinThresholdOfViewPort = stores => {
  // only for classic view
  if (getIsInPlaceMode(stores) || isSinglePageMode(stores)) return true;
  return getViewPortWidthWithoutPagesViewScrollbar(stores) > HIDE_HUD_ICONS_MIN_THRESHOLD;
};

export const getItemsForOverFlowMenu = stores => {
  const itemList = [];
  const extComponents = stores.hudStore.getRightAlignedExternalComponents;
  const DEF_POS = 1000; // Default max position value
  let posMin = DEF_POS;
  // In modern viewer, context menu items position priority: Print, download, legal notice.
  // So, assigned position according to that. Minimum position number has maximum priority
  const modernViewerEnabled = getShouldUseModernViewer(stores);
  extComponents &&
    extComponents.forEach(extComponent => {
      const component = extComponent.component;
      let isIconRequired = !!modernViewerEnabled;
      if (
        typeof component === "object" &&
        component.title &&
        component.clickHandler &&
        (isIconRequired ? component.icon : true)
      ) {
        if (component.position) {
          posMin = Math.min(posMin, component.position);
        } else {
          component.position = DEF_POS;
        }
        itemList.push({
          title: component.title,
          eventName: `overflowMenu${component.title}`,
          eventHandler: () => {
            component.clickHandler();
          },
          args: [],
          position: modernViewerEnabled ? component.position : 40,
          icon: component.icon,
          placement: contextMenuPlacements.CONTEXT_MENU_BODY
        });
      }
    });
  if (getIsInPlaceMode(stores) || (isSinglePageMode(stores) && modernViewerEnabled)) {
    if (showDownloadButton(stores)) {
      itemList.push({
        title: modernViewerEnabled ? "tooltip.modernViewer.download" : "contextMenu.downloadPdf",
        eventName: "saveas",
        eventHandler: () => {
          downloadAssetFromDocApi(stores, {
            triggeredFrom: "hud"
          });
        },
        args: [],
        position: modernViewerEnabled ? posMin - 1 : 40,
        icon: downloadIcon,
        placement: contextMenuPlacements.CONTEXT_MENU_BODY
      });
    }
    if (showPrint(stores)) {
      itemList.push({
        title: modernViewerEnabled ? "tooltip.modernViewer.print" : "contextMenu.printPdf",
        eventName: "print",
        eventHandler: () => {
          printHandler.print({
            triggeredFrom: "hud"
          });
        },
        args: [],
        position: modernViewerEnabled ? posMin - 2 : 40,
        icon: printIcon,
        placement: contextMenuPlacements.CONTEXT_MENU_BODY
      });
    }
  }
  return itemList;
};

export const _isRotateViewEnabled = stores => {
  const currentDocViewMode = getViewMode(stores);
  if (
    (currentDocViewMode === docViewModes.CLASSIC_VIEW ||
      currentDocViewMode === docViewModes.CONTINUOUS ||
      currentDocViewMode === docViewModes.NON_PDF ||
      isFitOneFullPageMode(stores)) &&
    (getFileExtension(stores) !== "pdf" ||
      getShouldUnblockUIFeatures(stores) ||
      getSwitchPreviewState(stores) !== SWITCH_PREVIEW_STATE.NONE)
  ) {
    return true;
  }
  return false;
};

export const isRotateEnabledInModernViewer = stores => stores.hudStore.enableRotationInModernViewer;

export const showRotateViewButton = stores => {
  if (_isRotateViewEnabled(stores)) {
    return stores.hudStore.componentProperties.get(appIdentifiers.ROTATE_VIEW_CONTROL).visible;
  }
  return false;
};

export const getComponentFromId = (stores, id) => {
  if (stores.hudStore.externalComponents.has(id)) {
    return stores.hudStore.externalComponents.get(id);
  }
  return null;
};

export const showOverflowMenuButton = stores => {
  // if any external right aligned component is present with title and clickHandler, show overflowmenu
  const isIconRequired = !!getShouldUseModernViewer(stores);
  const extComponents = stores.hudStore.getRightAlignedExternalComponents;
  if (extComponents) {
    for (let i = 0; i < extComponents.length; i++) {
      const component = extComponents[i].component;
      if (
        typeof component === "object" &&
        component.title &&
        component.clickHandler &&
        (isIconRequired ? component.icon : true)
      ) {
        return true;
      }
    }
  }
  return (
    (getIsInPlaceMode(stores) || (isSinglePageMode(stores) && getShouldUseModernViewer(stores))) &&
    showDownloadButton(stores) &&
    showPrint(stores)
  );
};

export const applyAnimationClasses = stores => stores.hudStore.applyAnimationClasses;
export const animationOutDuration = stores => stores.hudStore.animationOutDuration;
export const animationInDuration = stores => stores.hudStore.animationInDuration;

export const getHUDLeft = stores => stores.hudStore.left;

export const getDockedWidth = stores => stores.hudStore.dockedWidth;

export const getHUDWidth = stores => stores.hudStore.hudWidth;

export const showTwoColumnButton = stores =>
  getShouldUseModernViewer(stores) && stores.hudStore.componentProperties.get(appIdentifiers.TWO_COLUMN).visible;

export const showFullScreenButton = stores =>
  !isCurrentDeviceTablet() &&
  !isCurrentDeviceMobile() &&
  getShouldUseModernViewer(stores) &&
  stores.hudStore.componentProperties.get(appIdentifiers.FULL_SCREEN).visible;

export const showViewWithScrollButton = stores =>
  stores.hudStore.componentProperties.get(appIdentifiers.VIEW_WITH_SCROLL).visible;

export const showProgressBar = stores => {
  if (!isFirstPageLoaded(stores)) return false;
  if (isPageByPageViewMode(stores)) return true;
  return isSinglePageMode(stores) && getIsHUDDocked(stores) && (!hudDisabled(stores) || isHudAnimationOut(stores));
};
